import {Data, Seconds} from '@store/@types/util';
import User, {UserId} from 'records/User';
import {FileUploadId} from 'records/CoachingReviewFileUpload';
import {FileUploadState} from 'records/FileUploadState';
import {Map} from 'immutable';
import dayjs, {Dayjs} from 'dayjs';

export interface GptSummarization {
  summary: string;
  nextSteps: string[];
  qa: {question: string; answer: string}[];
}

export interface CallInterface {
  id: FileUploadId;
  caller: User;
  template: {
    lineOne: string;
    lineTwo: string;
    lineThree: string;
  };
  // Requesting user's vote state
  vote?: 'Up' | 'Down';
  // Total votes up
  votesUp: number;
  // Total votes down
  votesDown: number;
  // Number of scorecards scored
  scorecardResults: number;
  gpt: GptSummarization;

  state: FileUploadState;
  crmLink?: string;
  date: Dayjs;
  duration: Seconds;
  coachMe: boolean;

  // Unparsed search fields attached to the call
  rawFields: Map<string, any>;

  /**
   * @deprecated Relies on comments/highlights/scorecount from Search Results instead of
   * their proper endpoints. Only use this for call search displays where we don't have full
   * info for the call.
   * @link CallCardHeader - see coachingStatus here for how to compute this correctly.
   */
  coachingProvided: boolean;
}

export class Call extends Data<CallInterface>({
  caller: undefined,
  coachMe: undefined,
  coachingProvided: undefined,
  crmLink: undefined,
  date: undefined,
  id: undefined,
  scorecardResults: undefined,
  state: undefined,
  template: undefined,
  vote: undefined,
  votesDown: undefined,
  votesUp: undefined,
  duration: undefined,
  rawFields: undefined,
  gpt: undefined,
}) {
  constructor(props: SearchResultInterface | CallInterface) {
    if ('custom_content' in props) {
      super(searchResultsToCall(props));
    } else {
      super(props);
    }
  }
}

function searchResultsToCall(props: SearchResultInterface): CallInterface {
  const duration = props.fields.Duration;
  const hasNonCallerComments = props.fields.Comments?.some(c => c.user_id !== props.caller.id);
  const hasNonCallerHighlights = props.fields.Highlights?.some(c => c.user_id !== props.caller.id);
  const hasNonCallerScorecards =
    props.number_of_scorecard_results - props.number_of_caller_scorecard_results > 0;

  const gpt = {
    summary: props.fields.Summary ?? '',
    qa: props.fields['Q & A'] ?? [],
    nextSteps: props.fields['Next Steps']?.filter(x => x).map(s => s.trim()) ?? [],
  };

  return {
    caller: new User(props.caller.toJS ? props.caller.toJS() : props.caller),
    coachMe: props.fields['Coaching Requested'],
    coachingProvided: hasNonCallerScorecards || hasNonCallerHighlights || hasNonCallerComments,
    crmLink: props.fields['CRM Link'],
    date: dayjs(props.fields.Date),
    id: props.id,
    scorecardResults: props.number_of_scorecard_results,
    state: props.state,
    template: {
      lineOne: props.custom_content.metadata_line_one,
      lineTwo: props.custom_content.metadata_line_two,
      lineThree: props.custom_content.metadata_line_three,
    },
    vote: props.vote ? props.vote : undefined,
    votesDown: props.number_of_votes_down,
    votesUp: props.number_of_votes_up,
    duration: duration,
    rawFields: Map(props.fields),
    gpt,
  };
}

// Raw result from python search
interface SearchResultInterface {
  caller: User;
  custom_content: {
    metadata_line_one: string;
    metadata_line_two: string;
    metadata_line_three: string;
  };
  fields: {
    Comments: SearchComment[];
    Highlights: Highlight[];
    Date: string;
    Duration: Seconds;
    'CRM Link': string;
    'Coaching Requested': boolean;
    Summary?: string;
    'Next Steps'?: string[];
    'Q & A'?: {question: string; answer: string}[];
  };
  id: string;
  number_of_caller_scorecard_results: number;
  number_of_scorecard_results: number;
  number_of_votes_down: number;
  number_of_votes_up: number;
  state: FileUploadState;
  vote: null | 'Up' | 'Down';
}

interface Highlight {
  id: string;
  active: boolean;
  color: string;
  content: string;
  created: string;
  primary: boolean;
  // parse and multiply by 100 to get call %
  start: string;
  end: string;
  user_id: UserId;
}

interface SearchComment {
  active: boolean;
  created: string;
  id: string;
  location: string;
  message: string;
  user_id: UserId;
}
